import { defineStore, storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import type { Track } from '../types';
import usePositionTrackingStore from './position-tracking';
import useNavigationStructureStore from './navigation-structure';
import type { EnhancedTocItem } from '../types/navigation';

export type PlaylistItem = {
  spineIndex: number;
  spineId: string;
  href: string;
  tracks: Track[];
};

export type PlaylistItemWithTimings = PlaylistItem & {
  startTime: number; // cumulative start time in seconds
  endTime: number; // cumulative end time in seconds
  duration: number; // duration of this item in seconds
};

/**
 * PlaylistStore
 *
 * Manages the playlist structure and navigation for audio-only books (books without visual pages).
 *
 * Level 3 in the dependency hierarchy.
 */
const usePlaylistStore = defineStore('book-player-playlist', () => {
  const positionTrackingStore = usePositionTrackingStore();
  const navigationStructureStore = useNavigationStructureStore();
  const { enhancedToc, pages } = storeToRefs(navigationStructureStore);
  const { currentPlaylistIndex, currentTrackIndexInItem, currentPlaylistTime } = storeToRefs(positionTrackingStore);
  const { setCurrentPageIndex, setSpineIndex, setPlaylistPosition, setPositionPct } = positionTrackingStore;

  const playlist = ref<PlaylistItemWithTimings[]>([]);
  const totalDuration = ref(0);

  // Lookup maps for playlist-specific navigation
  // These maps hrefs to playlist indices and timestamps
  const tocHrefToTime = ref<Map<string, number>>(new Map());
  const pageHrefToTime = ref<Map<string, number>>(new Map());
  const tocHrefToPlaylistIndex = ref<Map<string, number>>(new Map());
  const pageHrefToPlaylistIndex = ref<Map<string, number>>(new Map());

  const isInitializing = ref(false);
  const resolutionProgress = ref(0);

  const currentPlaylistItem = computed(() => playlist.value[currentPlaylistIndex.value] || null);
  const totalPlaylistItems = computed(() => playlist.value.length);

  // Navigation state helpers
  const isAtStartOfPlaylist = computed(() => currentPlaylistIndex.value === 0);
  const isAtEndOfPlaylist = computed(() => currentPlaylistIndex.value >= totalPlaylistItems.value - 1);

  /**
   * Calculate the duration of a single track
   */
  const getTrackDuration = (track: Track): number => {
    const begin = track.audio.clipBegin ?? 0;
    const end = track.audio.clipEnd ?? 0;
    return Math.max(0, end - begin);
  };

  /**
   * Calculate cumulative timings for all playlist items
   */
  const calculateTimings = (items: PlaylistItem[]): PlaylistItemWithTimings[] => {
    let cumulativeTime = 0;

    return items.map((item) => {
      const itemDuration = item.tracks.reduce((sum, track) => sum + getTrackDuration(track), 0);

      const itemWithTimings: PlaylistItemWithTimings = {
        ...item,
        startTime: cumulativeTime,
        endTime: cumulativeTime + itemDuration,
        duration: itemDuration,
      };

      cumulativeTime += itemDuration;
      return itemWithTimings;
    });
  };

  /**
   * Find which playlist item contains a given timestamp
   */
  const getPlaylistIndexByTime = (targetTime: number): number => {
    const index = playlist.value.findIndex((item) => targetTime >= item.startTime && targetTime < item.endTime);
    return index >= 0 ? index : playlist.value.length - 1;
  };

  /**
   * Find which track within a playlist item contains a given time offset
   */
  const getTrackIndexByTimeOffset = (playlistItem: PlaylistItemWithTimings, timeIntoItem: number): number => {
    let trackTime = 0;

    for (let i = 0; i < playlistItem.tracks.length; i++) {
      const track = playlistItem.tracks[i];
      const trackDuration = getTrackDuration(track);

      if (timeIntoItem < trackTime + trackDuration) {
        return i;
      }
      trackTime += trackDuration;
    }

    return playlistItem.tracks.length - 1;
  };

  /**
   * Build ToC mapping to playlist indices and timestamps
   */
  const buildTocMappings = (): void => {
    const processItems = (items: EnhancedTocItem[]) => {
      items.forEach((item) => {
        const cleanHref = item.href.split('#')[0];

        const playlistIndex = playlist.value.findIndex((pItem) => {
          if (pItem.href === cleanHref || pItem.href === item.href) return true;
          if (pItem.href.includes(cleanHref)) return true;
          return cleanHref.endsWith(pItem.href);
        });

        if (playlistIndex >= 0) {
          const timestamp = playlist.value[playlistIndex].startTime;
          tocHrefToPlaylistIndex.value.set(item.href, playlistIndex);
          tocHrefToTime.value.set(item.href, timestamp);
        }

        // Process subitems recursively
        if (item.subitems?.length) {
          processItems(item.subitems);
        }
      });
    };

    processItems(enhancedToc.value);
  };

  /**
   * Build page list mapping to playlist indices and timestamps
   */
  const buildPageMappings = (): void => {
    pages.value.forEach((page) => {
      const cleanHref = page.href.split('#')[0];

      const playlistIndex = playlist.value.findIndex((pItem) => {
        if (pItem.href === cleanHref || pItem.href === page.href) return true;
        if (pItem.href.includes(cleanHref)) return true;
        if (cleanHref.endsWith(pItem.href)) return true;
        return false;
      });

      if (playlistIndex >= 0) {
        const timestamp = playlist.value[playlistIndex].startTime;
        pageHrefToPlaylistIndex.value.set(page.href, playlistIndex);
        pageHrefToTime.value.set(page.href, timestamp);
      }
    });
  };

  /**
   * Update PositionTrackingStore with the current position
   * Similar to how RenditionStore updates position after navigation
   */
  const updatePositionTracking = (playlistIndex: number, trackIndex: number = 0, time: number = 0): void => {
    const item = playlist.value[playlistIndex];
    if (!item) return;

    // Calculate position percentage based on time
    const positionPct = totalDuration.value > 0 ? (time / totalDuration.value) * 100 : 0;

    // Update position tracking store - the single source of truth
    setSpineIndex(item.spineIndex);
    setPositionPct(positionPct);
    setPlaylistPosition(playlistIndex, trackIndex, time);

    // IMPORTANT: Call this AFTER setSpineIndex
    // For audio-only books (no pages), currentPageIndex represents the playlist index
    setCurrentPageIndex(playlistIndex);
  };

  /**
   * Get playlist index for a ToC href
   */
  const getPlaylistIndexForTocHref = (href: string): number | null => {
    const index = tocHrefToPlaylistIndex.value.get(href);
    return index !== undefined ? index : null;
  };

  /**
   * Get a playlist index for a page href
   */
  const getPlaylistIndexForPageHref = (href: string): number | null => {
    const index = pageHrefToPlaylistIndex.value.get(href);
    return index !== undefined ? index : null;
  };

  /**
   * Get playlist index for a page index (0-based index into pages array)
   */
  const getPlaylistIndexForPageIndex = (pageIndex: number): number | null => {
    if (pageIndex < 0 || pageIndex >= pages.value.length) return null;

    const page = pages.value[pageIndex];
    if (!page) return null;

    return getPlaylistIndexForPageHref(page.href);
  };

  /**
   * Get timestamp for a ToC href
   */
  const getTimestampForTocHref = (href: string): number | null => {
    const time = tocHrefToTime.value.get(href);
    return time !== undefined ? time : null;
  };

  /**
   * Get timestamp for a page href
   */
  const getTimestampForPageHref = (href: string): number | null => {
    const time = pageHrefToTime.value.get(href);
    return time !== undefined ? time : null;
  };

  /**
   * Initialize the playlist navigation system
   * Note: Assumes progress is already at 50% from track loading phase
   * Also assumes NavigationStructureStore has already been initialized with ToC and Pages
   */
  const initializePlaylist = (playlistItems: PlaylistItem[]) => {
    try {
      // Calculate total work units for progress tracking (remaining 50%)
      const totalTocItems = enhancedToc.value.length;
      const totalPageItems = pages.value.length;
      const totalWorkUnits = 1 + totalTocItems + totalPageItems;
      let unitsCompleted = 0;

      const tickProgress = (amount = 1) => {
        unitsCompleted += amount;
        if (totalWorkUnits > 0) {
          resolutionProgress.value = 50 + Math.round((unitsCompleted / totalWorkUnits) * 50);
        }
      };

      // Calculate timings for all playlist items
      playlist.value = calculateTimings(playlistItems);
      totalDuration.value = playlist.value.length > 0 ? playlist.value[playlist.value.length - 1].endTime : 0;
      tickProgress();

      if (enhancedToc.value.length > 0) {
        buildTocMappings();
        tickProgress(totalTocItems);
      }

      if (pages.value.length > 0) {
        buildPageMappings();
        tickProgress(totalPageItems);
      }

      updatePositionTracking(0, 0, 0);
    } finally {
      resolutionProgress.value = 100;
      isInitializing.value = false;
    }
  };

  /**
   * Navigate to a specific time in the audiobook
   */
  const goToTime = (targetTime: number): { playlistIndex: number; trackIndex: number; positionInTrack: number } => {
    const clampedTime = Math.max(0, Math.min(targetTime, totalDuration.value));

    const playlistIndex = getPlaylistIndexByTime(clampedTime);
    const item = playlist.value[playlistIndex];

    if (!item) {
      return { playlistIndex: 0, trackIndex: 0, positionInTrack: 0 };
    }

    const timeIntoItem = clampedTime - item.startTime;
    const trackIndex = getTrackIndexByTimeOffset(item, timeIntoItem);

    let trackTime = 0;
    for (let i = 0; i < trackIndex; i++) {
      trackTime += getTrackDuration(item.tracks[i]);
    }

    const track = item.tracks[trackIndex];
    const timeIntoTrack = timeIntoItem - trackTime;
    const positionInTrack = (track.audio.clipBegin ?? 0) + timeIntoTrack;

    updatePositionTracking(playlistIndex, trackIndex, clampedTime);

    return { playlistIndex, trackIndex, positionInTrack };
  };

  /**
   * Navigate to a specific playlist index
   */
  const goToPlaylistIndex = (index: number) => {
    if (index >= 0 && index < playlist.value.length) {
      updatePositionTracking(index, 0, playlist.value[index].startTime);
      return true;
    }
    return false;
  };

  /**
   * Navigate to a ToC href
   */
  const goToTocHref = (href: string): boolean => {
    const playlistIndex = getPlaylistIndexForTocHref(href);
    if (playlistIndex !== null) {
      return goToPlaylistIndex(playlistIndex);
    }
    return false;
  };

  /**
   * Navigate to a page href
   */
  const goToPageHref = (href: string): boolean => {
    const playlistIndex = getPlaylistIndexForPageHref(href);
    if (playlistIndex !== null) {
      return goToPlaylistIndex(playlistIndex);
    }
    return false;
  };

  /**
   * Navigate to a specific page index (0-based index into pages array)
   */
  const goToPageIndex = (pageIndex: number): boolean => {
    if (pageIndex < 0 || pageIndex >= pages.value.length) return false;

    const page = pages.value[pageIndex];
    if (!page) return false;

    return goToPageHref(page.href);
  };

  /**
   * Update the current time (called during playback)
   */
  const setCurrentTime = (time: number) => {
    // Check if we've crossed into a new playlist item
    const newIndex = getPlaylistIndexByTime(time);
    const currentIndex = currentPlaylistIndex.value;

    // NOTE: Do NOT auto-advance to next playlist item here
    // The audio player's onTrackEnd event will handle segment navigation
    // Auto-advancing here causes double-jumps because both this function
    // and the navigation code try to increment the index

    // Always use the current index, just update the time
    updatePositionTracking(currentIndex, currentTrackIndexInItem.value, time);
  };

  const $reset = () => {
    playlist.value = [];
    totalDuration.value = 0;
    isInitializing.value = false;
    resolutionProgress.value = 0;
    tocHrefToTime.value.clear();
    pageHrefToTime.value.clear();
    tocHrefToPlaylistIndex.value.clear();
    pageHrefToPlaylistIndex.value.clear();
  };

  return {
    playlist,
    totalPlaylistItems,
    currentPlaylistItem,
    isInitializing,
    resolutionProgress,
    totalDuration,
    isAtStartOfPlaylist,
    isAtEndOfPlaylist,

    // From PositionTrackingStore for convenience
    currentPlaylistIndex,
    currentTrackIndexInItem,
    currentPlaylistTime,

    initializePlaylist,
    goToPlaylistIndex,
    goToTocHref,
    goToPageHref,
    goToPageIndex,
    goToTime,
    setCurrentTime,
    getPlaylistIndexForTocHref,
    getPlaylistIndexForPageHref,
    getPlaylistIndexForPageIndex,
    getTimestampForTocHref,
    getTimestampForPageHref,
    $reset,
  };
});

export default usePlaylistStore;
